import { useState, useEffect, useCallback } from 'react'
import { useFormData, useFormActions, useFormStore, useIdentityDocumentsStatus } from '@hooks/useFormStore'
import { submissionsApi, analyticsApi, identityDocumentsApi, operatorApi, ApiError } from '@services/api'
import { useOperatorAuth } from '@hooks/useOperatorAuth'
import { Button } from '@components/UI/Button'
import { clsx } from 'clsx'
import { isIdentityDocumentRequired, isIdentityAttachmentRequired, type FormStep, type FormData } from '@/types'
import { SendToClientModal } from './SendToClientModal'
import styles from '../Form/FormSection.module.css'

interface MissingField {
  field: string
  label: string
  step: FormStep
}

/**
 * Validazione preventiva lato operatore: identifica il primo campo mancante
 * cosi' da mostrare un messaggio puntuale e offrire il bottone "Vai a [step]".
 * Speculare alla validazione backend in `prepareSubmission`.
 */
function findFirstMissingField(
  formData: FormData,
  documentRequired: boolean,
  attachmentRequired: boolean,
  hasRequiredDocs: boolean,
  clientWillUpload: boolean,
): MissingField | null {
  const isBusiness = formData.tipo_cliente === 'business'

  if (isBusiness) {
    if (!formData.ragione_sociale) return { field: 'ragione_sociale', label: 'Ragione sociale', step: 'anagrafica' }
    if (!formData.partita_iva) return { field: 'partita_iva', label: 'Partita IVA', step: 'anagrafica' }
    if (!formData.rappresentante_legale_nome) return { field: 'rappresentante_legale_nome', label: 'Nome rappresentante legale', step: 'anagrafica' }
    if (!formData.rappresentante_legale_cognome) return { field: 'rappresentante_legale_cognome', label: 'Cognome rappresentante legale', step: 'anagrafica' }
    if (!formData.rappresentante_legale_cf) return { field: 'rappresentante_legale_cf', label: 'CF rappresentante legale', step: 'anagrafica' }
  } else {
    if (!formData.nome) return { field: 'nome', label: 'Nome', step: 'anagrafica' }
    if (!formData.cognome) return { field: 'cognome', label: 'Cognome', step: 'anagrafica' }
    if (!formData.codice_fiscale) return { field: 'codice_fiscale', label: 'Codice fiscale', step: 'anagrafica' }
  }

  if (!formData.telefono) return { field: 'telefono', label: 'Telefono', step: 'anagrafica' }
  if (!formData.email) return { field: 'email', label: 'Email', step: 'anagrafica' }

  if (!formData.indirizzo_residenza) return { field: 'indirizzo_residenza', label: 'Indirizzo di residenza', step: 'anagrafica' }
  if (!formData.cap_residenza) return { field: 'cap_residenza', label: 'CAP residenza', step: 'anagrafica' }
  if (!formData.citta_residenza) return { field: 'citta_residenza', label: 'Citta residenza', step: 'anagrafica' }
  if (!formData.provincia_residenza) return { field: 'provincia_residenza', label: 'Provincia residenza', step: 'anagrafica' }

  if (!formData.tipo_fornitura) return { field: 'tipo_fornitura', label: 'Tipo fornitura', step: 'fornitura' }

  if (formData.ha_luce) {
    if (!formData.pod_luce) return { field: 'pod_luce', label: 'POD (luce)', step: 'fornitura' }
    if (formData.consumo_annuo_luce == null || formData.consumo_annuo_luce <= 0) {
      return { field: 'consumo_annuo_luce', label: 'Consumo annuo luce', step: 'fornitura' }
    }
    if (!formData.tipo_attivazione_luce) return { field: 'tipo_attivazione_luce', label: 'Tipo attivazione luce', step: 'fornitura' }
    if (!formData.prodotto_luce_id) return { field: 'prodotto_luce_id', label: 'Prodotto luce', step: 'fornitura' }
  }

  if (formData.ha_gas) {
    if (!formData.pdr_gas) return { field: 'pdr_gas', label: 'PDR (gas)', step: 'fornitura' }
    if (formData.consumo_annuo_gas == null || formData.consumo_annuo_gas < 500) {
      return { field: 'consumo_annuo_gas', label: 'Consumo annuo gas (min 500 Smc)', step: 'fornitura' }
    }
    if (!formData.tipo_attivazione_gas) return { field: 'tipo_attivazione_gas', label: 'Tipo attivazione gas', step: 'fornitura' }
    if (!formData.prodotto_gas_id) return { field: 'prodotto_gas_id', label: 'Prodotto gas', step: 'fornitura' }
  }

  if (!formData.metodo_pagamento || (formData.metodo_pagamento !== 'iban' && formData.metodo_pagamento !== 'pagoPA')) {
    return { field: 'metodo_pagamento', label: 'Metodo di pagamento', step: 'pagamento' }
  }
  if (formData.metodo_pagamento === 'iban' && !formData.iban) {
    return { field: 'iban', label: 'IBAN', step: 'pagamento' }
  }

  if (!formData.consenso_privacy) {
    return { field: 'consenso_privacy', label: 'Consenso privacy', step: 'riepilogo' }
  }

  if (documentRequired) {
    if (!formData.tipo_documento) return { field: 'tipo_documento', label: 'Tipo documento', step: 'documento' }
    if (!formData.numero_documento) return { field: 'numero_documento', label: 'Numero documento', step: 'documento' }
    if (!formData.data_scadenza_documento) return { field: 'data_scadenza_documento', label: 'Data scadenza documento', step: 'documento' }
  }

  if (attachmentRequired && !hasRequiredDocs && !clientWillUpload) {
    return { field: 'identity_document', label: 'Allegato fronte/retro documento', step: 'documento' }
  }

  return null
}

/** Mappa errori restituiti dal backend al formato UI (label + step). */
const BACKEND_FIELD_MAP: Record<string, { label: string; step: FormStep }> = {
  // Domestico
  nome: { label: 'Nome', step: 'anagrafica' },
  cognome: { label: 'Cognome', step: 'anagrafica' },
  codice_fiscale: { label: 'Codice fiscale', step: 'anagrafica' },
  // Business
  ragione_sociale: { label: 'Ragione sociale', step: 'anagrafica' },
  partita_iva: { label: 'Partita IVA', step: 'anagrafica' },
  rappresentante_legale_nome: { label: 'Nome rappresentante legale', step: 'anagrafica' },
  rappresentante_legale_cognome: { label: 'Cognome rappresentante legale', step: 'anagrafica' },
  rappresentante_legale_cf: { label: 'CF rappresentante legale', step: 'anagrafica' },
  // Contatti / indirizzo (condivisi)
  telefono: { label: 'Telefono', step: 'anagrafica' },
  email: { label: 'Email', step: 'anagrafica' },
  indirizzo_residenza: { label: 'Indirizzo residenza', step: 'anagrafica' },
  cap_residenza: { label: 'CAP residenza', step: 'anagrafica' },
  citta_residenza: { label: 'Citta residenza', step: 'anagrafica' },
  pod_luce: { label: 'POD (luce)', step: 'fornitura' },
  pdr_gas: { label: 'PDR (gas)', step: 'fornitura' },
  consumo_annuo_luce: { label: 'Consumo annuo luce', step: 'fornitura' },
  consumo_annuo_gas: { label: 'Consumo annuo gas', step: 'fornitura' },
  metodo_pagamento: { label: 'Metodo di pagamento', step: 'pagamento' },
  iban: { label: 'IBAN', step: 'pagamento' },
  consenso_privacy: { label: 'Consenso privacy', step: 'riepilogo' },
  tipo_documento: { label: 'Tipo documento', step: 'documento' },
  numero_documento: { label: 'Numero documento', step: 'documento' },
  data_scadenza_documento: { label: 'Data scadenza documento', step: 'documento' },
  identity_document: { label: 'Allegato documento', step: 'documento' },
}

interface OperatorRiepilogoSectionProps {
  onSentToClient?: (submissionId: number) => void
}

export function OperatorRiepilogoSection({ onSentToClient }: OperatorRiepilogoSectionProps) {
  const formData = useFormData()
  const { prevStep, setStep, setData, setSubmissionId, setIdentityDocumentsStatus } = useFormActions()
  const submissionId = useFormStore((state) => state.submissionId)
  const ocrAssisted = useFormStore((state) => state.ocrAssisted)
  const identityDocsStatus = useIdentityDocumentsStatus()
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [missingField, setMissingField] = useState<MissingField | null>(null)
  const [showSendModal, setShowSendModal] = useState(false)
  const [currentSubmissionId, setCurrentSubmissionId] = useState<number | null>(submissionId)

  // VAS "Hera NoProblem": la selezione avviene nello step Fornitura (solo
  // operatori Adventure); qui la persistiamo sulla submission prima dell'invio.
  const { operator } = useOperatorAuth()
  const vasEnabled = !!operator?.vas_enabled
  
  const isBusiness = formData.tipo_cliente === 'business'

  // Operator mode: estremi documento sempre richiesti. L'allegato fronte/retro
  // non serve sulle pratiche di solo switch (dal 21/09/2026), e resta comunque
  // saltabile se l'operatore delega l'upload al cliente (client_must_upload_identity).
  const documentRequired = isIdentityDocumentRequired(formData, 'operator')
  const attachmentRequired = isIdentityAttachmentRequired(formData, 'operator')
  const hasRequiredDocs = identityDocsStatus?.has_required_documents ?? false
  const clientWillUpload = !!formData.client_must_upload_identity
  const canSubmit = !attachmentRequired || hasRequiredDocs || clientWillUpload

  const handleToggleClientWillUpload = (next: boolean) => {
    setData({ client_must_upload_identity: next })
    if (next) {
      setError(null)
      setMissingField(null)
    }
    if (submissionId) {
      submissionsApi
        .update(submissionId, { client_must_upload_identity: next })
        .catch((err) => {
          console.error('Persist client_must_upload_identity failed:', err)
        })
    }
  }
  
  // Refresh identity documents status on mount
  const refreshIdentityDocsStatus = useCallback(async () => {
    if (documentRequired) {
      try {
        const status = await identityDocumentsApi.getStatus()
        setIdentityDocumentsStatus(status)
      } catch (error) {
        console.error('Error fetching identity documents status:', error)
      }
    }
  }, [documentRequired, setIdentityDocumentsStatus])

  useEffect(() => {
    refreshIdentityDocsStatus()
  }, [refreshIdentityDocsStatus])

  const handlePrepareAndSend = async () => {
    // Pre-flight: cerca il primo campo mancante e blocca con messaggio puntuale.
    const missing = findFirstMissingField(formData, documentRequired, attachmentRequired, hasRequiredDocs, clientWillUpload)
    if (missing) {
      setMissingField(missing)
      setError(`Manca un campo obbligatorio: ${missing.label}.`)
      return
    }

    setIsSubmitting(true)
    setError(null)
    setMissingField(null)

    try {
      let currentId = submissionId

      if (currentId) {
        await submissionsApi.update(currentId, formData)
        await submissionsApi.prepare(currentId)
      } else {
        const { id } = await submissionsApi.create({
          ...formData
        })
        currentId = id
        setSubmissionId(id)
        // Dopo il create facciamo un update completo: la whitelist del POST e' piu'
        // ristretta di quella del PUT (es. flag operatore client_must_upload_identity),
        // quindi forziamo l'allineamento dei campi prima del prepare.
        await submissionsApi.update(id, formData)
        await submissionsApi.prepare(id)
      }

      analyticsApi.track('submit', 'operator_form_prepared', {
        data: {
          tipo_fornitura: formData.tipo_fornitura,
          ocr_assisted: ocrAssisted,
          mode: 'operator'
        }
      })

      // Persiste la selezione VAS (Adventure), scelta nello step Fornitura,
      // prima dell'invio al cliente.
      if (vasEnabled && currentId) {
        try {
          await operatorApi.setVasSelection(currentId, {
            luce: !!formData.vas_luce,
            gas: !!formData.vas_gas,
          })
        } catch (vasErr) {
          console.error('Persist VAS selection failed:', vasErr)
        }
      }

      setCurrentSubmissionId(currentId)
      setShowSendModal(true)
    } catch (err) {
      // Se il backend ha mandato 422 con la lista errori, prendiamo il primo
      // campo mappato e indirizziamo l'operatore allo step corretto.
      if (err instanceof ApiError && err.statusCode === 422 && err.errors && Object.keys(err.errors).length > 0) {
        const firstKey = Object.keys(err.errors)[0]
        const mapped = BACKEND_FIELD_MAP[firstKey]
        if (mapped) {
          setMissingField({ field: firstKey, label: mapped.label, step: mapped.step })
          setError(`${err.errors[firstKey]}`)
        } else {
          setError(`${err.errors[firstKey]}`)
        }
      } else {
        const message = err instanceof ApiError && err.statusCode === 401
          ? 'Sessione operatore scaduta. Effettua di nuovo il login e riprova.'
          : err instanceof Error
            ? err.message
            : 'Errore durante la preparazione'
        setError(message)
      }
      analyticsApi.trackError(error || 'submit error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSendSuccess = (newSubmissionId: number) => {
    setShowSendModal(false)
    onSentToClient?.(newSubmissionId)
  }

  const isLocatario = formData.titolarita_immobile === 'locatario'
    || formData.titolarita_immobile === 'locatario_edilizia_pubblica'
    || formData.titolarita_immobile === 'comodatario'

  const titolaritaLabel = (value: string | null) => {
    switch (value) {
      case 'proprietario': return 'Proprietario'
      case 'locatario': return 'Locatario'
      case 'locatario_edilizia_pubblica': return 'Locatario Edilizia Pubblica'
      case 'comodatario': return 'Comodatario'
      case 'usufruttuario': return 'Usufruttuario'
      case 'altro': return 'Altro'
      default: return '-'
    }
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <i className="bi bi-check-circle-fill" />
        </div>
        <div>
          <h2 className={styles.sectionTitle}>Riepilogo</h2>
          <p className={styles.sectionDescription}>
            Verifica i dati prima di inviare al cliente per la firma
          </p>
        </div>
      </div>

      {/* Badge Modalità Operatore */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.75rem 1rem',
        background: isBusiness
          ? 'linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%)'
          : 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)',
        borderRadius: '0.5rem',
        marginBottom: '1.5rem',
        border: isBusiness ? '1px solid #c4b5fd' : '1px solid #93c5fd'
      }}>
        <i className={isBusiness ? 'bi bi-building' : 'bi bi-headset'}
           style={{ color: isBusiness ? '#7c3aed' : '#2563eb', fontSize: '1.25rem' }} />
        <div>
          <strong style={{ color: isBusiness ? '#5b21b6' : '#1e40af', fontSize: '0.875rem' }}>
            Modalità Operatore {isBusiness ? '- Business' : '- Domestico'}
          </strong>
          <p style={{ color: isBusiness ? '#8b5cf6' : '#3b82f6', fontSize: '0.75rem', margin: 0 }}>
            Dopo l&apos;invio, il cliente riceverà email o SMS con il link di firma (non visibile agli operatori)
          </p>
        </div>
      </div>

      {/* Dati Azienda (solo business) */}
      {isBusiness && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <i className="bi bi-building" />
            <span>Dati Azienda</span>
            <button type="button" className={styles.editButton} onClick={() => setStep('anagrafica')} disabled={isSubmitting}>
              <i className="bi bi-pencil" /> Modifica
            </button>
          </div>
          <div className={styles.summaryGrid}>
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Ragione Sociale</span>
              <span className={styles.summaryValue}>{formData.ragione_sociale}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Partita IVA</span>
              <span className={styles.summaryValue}>{formData.partita_iva}</span>
            </div>
            {formData.codice_fiscale_azienda && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Codice Fiscale Azienda</span>
                <span className={styles.summaryValue}>{formData.codice_fiscale_azienda}</span>
              </div>
            )}
            {formData.codice_sdi && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Codice SDI</span>
                <span className={styles.summaryValue}>{formData.codice_sdi}</span>
              </div>
            )}
            {formData.pec_azienda && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>PEC Azienda</span>
                <span className={styles.summaryValue}>{formData.pec_azienda}</span>
              </div>
            )}
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Sede Legale</span>
              <span className={styles.summaryValue}>
                {formData.sede_legale_indirizzo}{formData.sede_legale_civico ? ` ${formData.sede_legale_civico}` : ''}, {formData.sede_legale_cap} {formData.sede_legale_citta} ({formData.sede_legale_provincia})
              </span>
            </div>
            {formData.rappresentante_legale_cf && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>CF Rappresentante Legale</span>
                <span className={styles.summaryValue}>{formData.rappresentante_legale_cf}</span>
              </div>
            )}
            {formData.agevolazione_iva && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Agevolazione IVA</span>
                <span className={styles.summaryValue}>{formData.agevolazione_iva}</span>
              </div>
            )}
            {formData.agevolazione_accise && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Agevolazione Accise</span>
                <span className={styles.summaryValue}>{formData.agevolazione_accise}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dati Anagrafici / Rappresentante Legale */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-person" />
          <span>{isBusiness ? 'Rappresentante Legale' : 'Dati Anagrafici'}</span>
          <button 
            type="button"
            className={styles.editButton}
            onClick={() => setStep('anagrafica')}
            disabled={isSubmitting}
          >
            <i className="bi bi-pencil" />
            Modifica
          </button>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Nome e Cognome</span>
            <span className={styles.summaryValue}>
              {formData.nome} {formData.cognome}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Codice Fiscale</span>
            <span className={styles.summaryValue}>{formData.codice_fiscale}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Telefono</span>
            <span className={styles.summaryValue}>{formData.telefono}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Email</span>
            <span className={styles.summaryValue}>{formData.email}</span>
          </div>
          {formData.data_nascita && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Data e Luogo di Nascita</span>
              <span className={styles.summaryValue}>
                {formData.data_nascita}{formData.luogo_nascita ? ` - ${formData.luogo_nascita}` : ''}{formData.provincia_nascita ? ` (${formData.provincia_nascita})` : ''}
              </span>
            </div>
          )}
          {formData.pec && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>PEC</span>
              <span className={styles.summaryValue}>{formData.pec}</span>
            </div>
          )}
          <div className={clsx(styles.summaryItem, styles.fullWidth)}>
            <span className={styles.summaryLabel}>
              {isBusiness ? 'Indirizzo Rappresentante' : 'Indirizzo di Residenza'}
            </span>
            <span className={styles.summaryValue}>
              {formData.indirizzo_residenza}, {formData.cap_residenza} {formData.citta_residenza} ({formData.provincia_residenza})
            </span>
          </div>
          <div className={clsx(styles.summaryItem, styles.fullWidth)}>
            <span className={styles.summaryLabel}>Indirizzo di Fornitura</span>
            <span className={styles.summaryValue}>
              {formData.stesso_indirizzo ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="bi bi-check-circle-fill" style={{ color: 'var(--color-success-500)', fontSize: '0.875rem' }} />
                  {isBusiness ? 'Coincide con la sede legale' : 'Coincide con l\'indirizzo di residenza'}
                </span>
              ) : (
                `${formData.indirizzo_fornitura}, ${formData.cap_fornitura} ${formData.citta_fornitura} (${formData.provincia_fornitura})`
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Dati Fornitura */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-lightning-charge" />
          <span>Fornitura</span>
          <button 
            type="button"
            className={styles.editButton}
            onClick={() => setStep('fornitura')}
            disabled={isSubmitting}
          >
            <i className="bi bi-pencil" />
            Modifica
          </button>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Tipo</span>
            <span className={styles.summaryValue}>
              {formData.tipo_fornitura === 'entrambi' ? 'Luce + Gas' : 
               formData.tipo_fornitura === 'luce' ? 'Solo Luce' : 'Solo Gas'}
            </span>
          </div>
          
          {formData.ha_luce && (
            <>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>POD</span>
                <span className={styles.summaryValue}>{formData.pod_luce || '-'}</span>
              </div>
              {formData.potenza_impegnata && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Potenza</span>
                  <span className={styles.summaryValue}>{formData.potenza_impegnata} kW</span>
                </div>
              )}
              {formData.tipo_attivazione_luce && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Tipo Attivazione Luce</span>
                  <span className={styles.summaryValue}>
                    {formData.tipo_attivazione_luce === 'cambio_fornitore_voltura' && 'Cambio Fornitore con Voltura'}
                    {formData.tipo_attivazione_luce === 'cambio_fornitore' && 'Cambio Fornitore'}
                  </span>
                </div>
              )}
              {formData.consumo_annuo_luce != null && formData.consumo_annuo_luce > 0 && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Consumo Annuo Luce</span>
                  <span className={styles.summaryValue}>{formData.consumo_annuo_luce} kWh/anno</span>
                </div>
              )}
              {formData.fornitore_attuale_luce && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Fornitore Attuale Luce</span>
                  <span className={styles.summaryValue}>{formData.fornitore_attuale_luce}</span>
                </div>
              )}
              {formData.vecchio_intestatario_luce && (
                <div className={clsx(styles.summaryItem, styles.fullWidth)}>
                  <span className={styles.summaryLabel}>Vecchio Intestatario Luce</span>
                  <span className={styles.summaryValue}>
                    {formData.vecchio_intestatario_luce.nome} {formData.vecchio_intestatario_luce.cognome} - CF: {formData.vecchio_intestatario_luce.codice_fiscale}
                  </span>
                </div>
              )}
            </>
          )}
          
          {formData.ha_gas && (
            <>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>PDR</span>
                <span className={styles.summaryValue}>{formData.pdr_gas || '-'}</span>
              </div>
              {formData.matricola_contatore && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Matricola</span>
                  <span className={styles.summaryValue}>{formData.matricola_contatore}</span>
                </div>
              )}
              {formData.tipo_attivazione_gas && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Tipo Attivazione Gas</span>
                  <span className={styles.summaryValue}>
                    {formData.tipo_attivazione_gas === 'cambio_fornitore_voltura' && 'Cambio Fornitore con Voltura'}
                    {formData.tipo_attivazione_gas === 'cambio_fornitore' && 'Cambio Fornitore'}
                  </span>
                </div>
              )}
              {formData.consumo_annuo_gas != null && formData.consumo_annuo_gas > 0 && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Consumo Annuo Gas</span>
                  <span className={styles.summaryValue}>{formData.consumo_annuo_gas} Smc/anno</span>
                </div>
              )}
              {formData.fornitore_attuale_gas && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Fornitore Attuale Gas</span>
                  <span className={styles.summaryValue}>{formData.fornitore_attuale_gas}</span>
                </div>
              )}
              {formData.categoria_uso_gas && formData.categoria_uso_gas.length > 0 && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Categoria Uso Gas</span>
                  <span className={styles.summaryValue}>
                    {formData.categoria_uso_gas.map(c =>
                      c === 'cottura' ? 'Cottura' :
                      c === 'acqua_calda' ? 'Acqua Calda' :
                      c === 'riscaldamento_autonomo' ? 'Risc. Autonomo' :
                      c === 'riscaldamento_centralizzato' ? 'Risc. Centralizzato' : c
                    ).join(', ')}
                  </span>
                </div>
              )}
              {formData.vecchio_intestatario_gas && (
                <div className={clsx(styles.summaryItem, styles.fullWidth)}>
                  <span className={styles.summaryLabel}>Vecchio Intestatario Gas</span>
                  <span className={styles.summaryValue}>
                    {formData.vecchio_intestatario_gas.nome} {formData.vecchio_intestatario_gas.cognome} - CF: {formData.vecchio_intestatario_gas.codice_fiscale}
                  </span>
                </div>
              )}
            </>
          )}

          {formData.prodotto_luce_id && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Prodotto Luce</span>
              <span className={styles.summaryValue}>ID: {formData.prodotto_luce_id}</span>
            </div>
          )}
          {formData.prodotto_gas_id && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Prodotto Gas</span>
              <span className={styles.summaryValue}>ID: {formData.prodotto_gas_id}</span>
            </div>
          )}
          {vasEnabled && formData.vas_luce && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Servizio aggiuntivo Luce</span>
              <span className={styles.summaryValue}>Hera NoProblem Luce</span>
            </div>
          )}
          {vasEnabled && formData.vas_gas && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Servizio aggiuntivo Gas</span>
              <span className={styles.summaryValue}>Hera NoProblem Gas</span>
            </div>
          )}
          {formData.company && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Società</span>
              <span className={styles.summaryValue}>
                {formData.company === 'heracomm' ? 'Heracomm' : 'EstEnergy'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Pagamento */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-credit-card" />
          <span>Pagamento</span>
          <button 
            type="button"
            className={styles.editButton}
            onClick={() => setStep('pagamento')}
            disabled={isSubmitting}
          >
            <i className="bi bi-pencil" />
            Modifica
          </button>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Metodo</span>
            <span className={styles.summaryValue}>
              {formData.metodo_pagamento === 'iban'
                ? 'Addebito su conto'
                : formData.metodo_pagamento === 'pagoPA'
                  ? 'Bollettino PagoPA'
                  : '—'}
            </span>
          </div>
          {formData.metodo_pagamento === 'iban' && formData.iban && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>IBAN</span>
              <span className={styles.summaryValue}>{formData.iban}</span>
            </div>
          )}
          {formData.metodo_pagamento === 'iban' && formData.bic_swift && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>BIC / SWIFT</span>
              <span className={styles.summaryValue}>{formData.bic_swift}</span>
            </div>
          )}
          {formData.metodo_pagamento === 'iban' && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Intestatario IBAN</span>
              <span className={styles.summaryValue}>
                {formData.iban_intestato_a_me ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <i className="bi bi-check-circle-fill" style={{ color: 'var(--color-success-500)', fontSize: '0.875rem' }} />
                    Intestato al titolare
                  </span>
                ) : formData.intestatario_iban ? (
                  `${formData.intestatario_iban.nome} ${formData.intestatario_iban.cognome} - CF: ${formData.intestatario_iban.codice_fiscale}`
                ) : '-'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Invio Bolletta */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-envelope" />
          <span>Invio Bolletta</span>
          <button 
            type="button"
            className={styles.editButton}
            onClick={() => setStep('pagamento')}
            disabled={isSubmitting}
          >
            <i className="bi bi-pencil" />
            Modifica
          </button>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Modalità</span>
            <span className={styles.summaryValue}>
              {formData.modalita_invio_bolletta === 'email' ? 'Email' : 'Posta'}
            </span>
          </div>
          {formData.modalita_invio_bolletta === 'email' && (
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Email Invio</span>
              <span className={styles.summaryValue}>
                {formData.invio_email_tipo === 'anagrafica' ? formData.email : formData.invio_email_altro}
              </span>
            </div>
          )}
          {formData.modalita_invio_bolletta === 'posta' && (
            <div className={clsx(styles.summaryItem, styles.fullWidth)}>
              <span className={styles.summaryLabel}>Indirizzo Invio</span>
              <span className={styles.summaryValue}>
                {formData.invio_indirizzo_tipo === 'residenza'
                  ? `${formData.indirizzo_residenza}, ${formData.cap_residenza} ${formData.citta_residenza} (${formData.provincia_residenza})`
                  : formData.invio_indirizzo_tipo === 'fornitura'
                  ? `${formData.indirizzo_fornitura}, ${formData.cap_fornitura} ${formData.citta_fornitura} (${formData.provincia_fornitura})`
                  : `${formData.invio_indirizzo_altro}, ${formData.invio_cap_altro} ${formData.invio_citta_altro} (${formData.invio_provincia_altro})`
                }
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Consensi - checkbox cliccabili direttamente nel riepilogo */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <i className="bi bi-shield-check" />
          <span>Consensi</span>
        </div>
        <div className={styles.consents}>
          <label className={styles.consent}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={!!formData.consenso_privacy}
              disabled={isSubmitting}
              onChange={() => setData({ consenso_privacy: !formData.consenso_privacy })}
            />
            <span className={styles.checkmark}>
              <i className="bi bi-check-lg" />
            </span>
            <span className={styles.consentText}>
              <strong>Accetto l'informativa sulla privacy</strong> e il trattamento dei dati personali ai sensi del GDPR *
            </span>
          </label>
          <label className={styles.consent}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={!!formData.consenso_marketing}
              disabled={isSubmitting}
              onChange={() => setData({ consenso_marketing: !formData.consenso_marketing })}
            />
            <span className={styles.checkmark}>
              <i className="bi bi-check-lg" />
            </span>
            <span className={styles.consentText}>
              Consenso marketing e comunicazioni commerciali
            </span>
          </label>
          <label className={styles.consent}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={!!formData.consenso_comunicazioni}
              disabled={isSubmitting}
              onChange={() => setData({ consenso_comunicazioni: !formData.consenso_comunicazioni })}
            />
            <span className={styles.checkmark}>
              <i className="bi bi-check-lg" />
            </span>
            <span className={styles.consentText}>
              Consenso comunicazioni variazioni contrattuali
            </span>
          </label>
        </div>
      </div>

      {/* Documento d'Identità - solo se richiesto */}
      {documentRequired && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <i className="bi bi-person-vcard" />
            <span>Documento d'Identità</span>
            <button 
              type="button"
              className={styles.editButton}
              onClick={() => setStep('documento')}
              disabled={isSubmitting || clientWillUpload}
            >
              <i className="bi bi-pencil" />
              Modifica
            </button>
          </div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Tipo Documento</span>
              <span className={styles.summaryValue}>
                {formData.tipo_documento === 'carta_identita' && "Carta d'Identità"}
                {formData.tipo_documento === 'patente' && "Patente"}
                {formData.tipo_documento === 'passaporto' && "Passaporto"}
                {!formData.tipo_documento && '-'}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Numero</span>
              <span className={styles.summaryValue}>{formData.numero_documento || '-'}</span>
            </div>
            {formData.data_emissione_documento && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Data Emissione</span>
                <span className={styles.summaryValue}>{formData.data_emissione_documento}</span>
              </div>
            )}
            {formData.data_scadenza_documento && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Data Scadenza</span>
                <span className={styles.summaryValue}>{formData.data_scadenza_documento}</span>
              </div>
            )}
            {formData.ente_rilascio && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Ente Rilascio</span>
                <span className={styles.summaryValue}>{formData.ente_rilascio}</span>
              </div>
            )}
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Allegato</span>
              <span className={styles.summaryValue}>
                {clientWillUpload && attachmentRequired ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-primary-600)' }}>
                    <i className="bi bi-cloud-upload-fill" />
                    Lo caricherà il cliente in fase di firma
                  </span>
                ) : hasRequiredDocs ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-success-600)' }}>
                    <i className="bi bi-check-circle-fill" />
                    Documento caricato ({identityDocsStatus?.count || 0} file)
                  </span>
                ) : attachmentRequired ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-danger-600)' }}>
                    <i className="bi bi-exclamation-triangle-fill" />
                    Non caricato
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-gray-500)' }}>
                    <i className="bi bi-dash-circle" />
                    Non richiesto per il cambio fornitore
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Toggle: documento caricato dal cliente (solo se l'allegato serve) */}
          {attachmentRequired && (
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '0.875rem 1rem',
                marginTop: '1rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--color-gray-200)',
                background: clientWillUpload ? 'var(--color-primary-50)' : 'var(--color-gray-50)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={clientWillUpload}
                onChange={(e) => handleToggleClientWillUpload(e.target.checked)}
                style={{ marginTop: '0.25rem' }}
                disabled={isSubmitting}
              />
              <span style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <strong style={{ color: 'var(--color-gray-900)', fontSize: '0.9rem' }}>
                  Non ho il documento del cliente
                </strong>
                <span style={{ color: 'var(--color-gray-600)', fontSize: '0.8125rem', lineHeight: 1.4 }}>
                  Il cliente caricherà fronte e retro durante il processo di firma online.
                  I dati del documento (numero, ente, scadenza) restano comunque a tuo carico.
                </span>
              </span>
            </label>
          )}
        </div>
      )}

      {/* Warning for missing identity document */}
      {attachmentRequired && !hasRequiredDocs && !clientWillUpload && (
        <div className={styles.warningBanner}>
          <i className="bi bi-exclamation-triangle-fill" />
          <div>
            <strong>Documento d'identità mancante</strong>
            <p>Carica fronte e retro dalla sezione Documento, oppure delega l'upload al cliente con la spunta qui sopra.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              icon="arrow-left"
              onClick={() => setStep('documento')}
              className={styles.warningButton}
            >
              Vai a Documento
            </Button>
          </div>
        </div>
      )}

      {/* Diario Consumi */}
      {formData.diario_consumi_attivo && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <i className="bi bi-bar-chart-line" />
            <span>Diario Consumi</span>
          </div>
          <div className={styles.summaryGrid}>
            {formData.superficie_abitazione != null && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Superficie Abitazione</span>
                <span className={styles.summaryValue}>{formData.superficie_abitazione} m²</span>
              </div>
            )}
            {formData.numero_abitanti != null && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Numero Abitanti</span>
                <span className={styles.summaryValue}>{formData.numero_abitanti}</span>
              </div>
            )}
            {formData.tipologia_abitazione && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Tipologia Abitazione</span>
                <span className={styles.summaryValue}>
                  {formData.tipologia_abitazione === 'appartamento' ? 'Appartamento' : 'Casa Indipendente'}
                </span>
              </div>
            )}
            {formData.proprietario_abitazione != null && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Proprietario</span>
                <span className={styles.summaryValue}>
                  {formData.proprietario_abitazione ? 'Sì' : 'No'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Titolarità Immobile */}
      {formData.titolarita_immobile && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <i className="bi bi-house-door" />
            <span>Titolarità Immobile</span>
          </div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Titolarità</span>
              <span className={styles.summaryValue}>{titolaritaLabel(formData.titolarita_immobile)}</span>
            </div>
            {(formData.catasto_foglio || formData.catasto_particella) && (
              <div className={clsx(styles.summaryItem, styles.fullWidth)}>
                <span className={styles.summaryLabel}>Dati Catastali</span>
                <span className={styles.summaryValue}>
                  {formData.catasto_sezione ? `Sez. ${formData.catasto_sezione} - ` : ''}
                  Foglio {formData.catasto_foglio || '-'}, Part. {formData.catasto_particella || '-'}
                  {formData.catasto_subalterno ? `, Sub. ${formData.catasto_subalterno}` : ''}
                </span>
              </div>
            )}
            {formData.titolarita_note && (
              <div className={clsx(styles.summaryItem, styles.fullWidth)}>
                <span className={styles.summaryLabel}>Note</span>
                <span className={styles.summaryValue}>{formData.titolarita_note}</span>
              </div>
            )}
            {isLocatario && (formData.locazione_agenzia_entrate || formData.locazione_numero) && (
              <>
                {formData.locazione_agenzia_entrate && (
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Agenzia Entrate</span>
                    <span className={styles.summaryValue}>{formData.locazione_agenzia_entrate}</span>
                  </div>
                )}
                {formData.locazione_data_registrazione && (
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Data Registrazione</span>
                    <span className={styles.summaryValue}>{formData.locazione_data_registrazione}</span>
                  </div>
                )}
                {formData.locazione_numero && (
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Numero Registrazione</span>
                    <span className={styles.summaryValue}>{formData.locazione_numero}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorBanner}>
          <i className="bi bi-exclamation-triangle" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
            <span>{error}</span>
            {missingField && (
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  icon="arrow-left"
                  onClick={() => {
                    setError(null)
                    setMissingField(null)
                    setStep(missingField.step)
                  }}
                >
                  Vai a {missingField.step}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          icon="arrow-left"
          onClick={prevStep}
          disabled={isSubmitting}
        >
          Indietro
        </Button>
        
        <Button
          type="button"
          icon="send"
          iconPosition="right"
          loading={isSubmitting}
          onClick={handlePrepareAndSend}
          disabled={!canSubmit || !formData.consenso_privacy}
        >
          Invia al Cliente
        </Button>
      </div>

      {/* Modal per invio al cliente */}
      <SendToClientModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        submissionId={currentSubmissionId}
        clientEmail={formData.email}
        clientPhone={formData.telefono}
        clientName={`${formData.nome} ${formData.cognome}`}
        onSuccess={handleSendSuccess}
      />
    </div>
  )
}
